import posthog from "posthog-js";

export type AnalyticsPropertyValue = string | number | boolean | null | undefined;
export type AnalyticsProperties = Record<string, AnalyticsPropertyValue>;

type AnalyticsBrowserWindow = Window & {
  __cordPosthogInitialized__?: boolean;
};

const POSTHOG_DEFAULTS_VERSION = "2026-01-30";
const POSTHOG_KEY = import.meta.env.VITE_PUBLIC_POSTHOG_KEY?.trim() ?? "";
const POSTHOG_HOST = import.meta.env.VITE_PUBLIC_POSTHOG_HOST?.trim() ?? "";
const BASE_ANALYTICS_PROPERTIES = {
  app_area: "web_app",
} as const;

export const ANALYTICS_EVENTS = {
  credentialFallbackConfirmationShown: "credential_fallback_confirmation_shown",
  credentialFallbackSuggested: "credential_fallback_suggested",
  credentialFallbackRequested: "credential_fallback_requested",
  credentialUsageRefreshRequested: "credential_usage_refresh_requested",
  continueInNewSessionRequested: "continue_in_new_session_requested",
  manualPrSubmitFailed: "manual_pr_submit_failed",
  manualPrSubmitted: "manual_pr_submitted",
  mergePrRequestFailed: "merge_pr_request_failed",
  mergePrRequested: "merge_pr_requested",
  prDescriptionGenerationRequested: "pr_description_generation_requested",
  quickPrRequestFailed: "quick_pr_request_failed",
  quickPrRequested: "quick_pr_requested",
  quickPrSyncRequested: "quick_pr_sync_requested",
  resolveMergeConflictRequestFailed: "resolve_merge_conflict_request_failed",
  resolveMergeConflictRequested: "resolve_merge_conflict_requested",
  startNewWorkRequested: "start_new_work_requested",
  threadArchived: "thread_archived",
  threadCreateFailed: "thread_create_failed",
  threadCreated: "thread_created",
  threadDeleteRequested: "thread_delete_requested",
  threadMessageSent: "thread_message_sent",
  threadUnarchived: "thread_unarchived",
  yoloRequestFailed: "yolo_request_failed",
  yoloRequested: "yolo_requested",
} as const;

function canUseBrowserWindow(): boolean {
  return typeof window !== "undefined";
}

function getAnalyticsWindow(): AnalyticsBrowserWindow | null {
  if (!canUseBrowserWindow()) return null;
  return window as AnalyticsBrowserWindow;
}

function isAnalyticsConfigured(): boolean {
  return POSTHOG_KEY.length > 0 && POSTHOG_HOST.length > 0;
}

function isAnalyticsInitialized(): boolean {
  return getAnalyticsWindow()?.__cordPosthogInitialized__ === true;
}

export function initializeAnalytics(): void {
  const analyticsWindow = getAnalyticsWindow();
  if (!analyticsWindow || !isAnalyticsConfigured()) return;
  if (analyticsWindow.__cordPosthogInitialized__ === true) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: true,
    capture_dead_clicks: true,
    capture_pageleave: true,
    capture_pageview: "history_change",
    defaults: POSTHOG_DEFAULTS_VERSION,
    person_profiles: "identified_only",
  });

  analyticsWindow.__cordPosthogInitialized__ = true;
}

function withAnalytics(action: () => void): void {
  initializeAnalytics();
  if (!isAnalyticsInitialized()) return;
  action();
}

export function captureAnalyticsEvent({
  event,
  properties,
}: {
  event: string;
  properties?: AnalyticsProperties;
}): void {
  withAnalytics(() => {
    posthog.capture(event, {
      ...BASE_ANALYTICS_PROPERTIES,
      ...(properties ?? {}),
    });
  });
}

export function identifyAnalyticsUser({
  email,
  name,
  organizationId,
  userId,
}: {
  email?: string | null;
  name?: string | null;
  organizationId?: string | null;
  userId: string;
}): void {
  withAnalytics(() => {
    const userProperties: AnalyticsProperties = {};

    if (email) {
      userProperties.email = email;
    }
    if (name) {
      userProperties.name = name;
    }
    if (organizationId) {
      userProperties.active_organization_id = organizationId;
      posthog.register({ active_organization_id: organizationId });
    } else {
      posthog.unregister("active_organization_id");
    }

    posthog.identify(userId, userProperties);
  });
}

export function resetAnalyticsUser(): void {
  if (!isAnalyticsInitialized()) return;
  posthog.reset();
}

export function buildThreadAnalyticsProperties({
  agentType,
  properties,
  repositoryId,
  sessionId,
  source,
}: {
  agentType?: string | null;
  properties?: AnalyticsProperties;
  repositoryId?: string | null;
  sessionId?: string | null;
  source?: string | null;
}): AnalyticsProperties {
  return {
    agent_type: agentType ?? null,
    repository_id: repositoryId ?? null,
    source: source ?? null,
    thread_id: sessionId ?? null,
    ...(properties ?? {}),
  };
}

export function buildPrAnalyticsProperties({
  agentType,
  prState,
  properties,
  repositoryId,
  sessionId,
  source,
}: {
  agentType?: string | null;
  prState?: string | null;
  properties?: AnalyticsProperties;
  repositoryId?: string | null;
  sessionId?: string | null;
  source?: string | null;
}): AnalyticsProperties {
  return buildThreadAnalyticsProperties({
    agentType,
    properties: {
      pr_state: prState ?? null,
      ...(properties ?? {}),
    },
    repositoryId,
    sessionId,
    source,
  });
}

initializeAnalytics();
